/**
 * monday-board-style table for template subitems (design doc: "The table (REQ-6)").
 *
 * One component serves both screens:
 *  - readOnly (template view): numbered rows, type-glyph headers, board-style cells
 *  - editable (Template Editor): adds drag-reorder, row delete, inline name input,
 *    "+ Add subitem" row, per-cell editing and the add-column / column menus —
 *    each enabled by passing the corresponding callback prop.
 *
 * Rows are the stored template shape: { id, name, column_values: [...], updates? }.
 * Columns: { id, title, type, settings? } — settings (parsed by columnSettingsOf) present
 * when the board is known, giving status cells their real colours.
 *
 * Like a monday group, every row carries a coloured bar on its left edge:
 * `groupColor` (monday's blue for a subitem template, the group's own colour
 * for a group template); `nested` tables (a group template's subitems) show it
 * lighter, the way monday shows subitems under their item.
 *
 * Updates sit at the right end of the name cell, in their own divided slot, as
 * on a monday board: an add-update bubble, or the update bubble with the row's
 * count. The slot renders whenever `onOpenUpdates` is given (editor) or any row
 * carries updates (template view), so templates saved before updates existed
 * look as before.
 */
import { IconButton } from '@vibe/core';
import {
  AddSmall,
  AddUpdate,
  Calendar,
  Checkbox as CheckboxIcon,
  Column as ColumnIcon,
  Delete,
  Drag,
  DropdownChevronDown,
  DropdownChevronRight,
  Dropdown as DropdownIcon,
  Link as LinkIcon,
  Favorite,
  Numbers,
  Person,
  Status as StatusIcon,
  Text as TextIcon,
  Timeline as TimelineIcon,
  Update as UpdateIcon,
} from '@vibe/icons';
import React, { useEffect, useRef, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { canonicalType } from '../../Config';
import { parseColumnValue } from '../../lib/columnValues';
import DynamicTextField, { TokenText } from '../DynamicTextField';
import CellContent, { statusColorFor } from './CellContent';
import './SubitemsTable.css';

// keyed by CANONICAL type (see Config.canonicalType)
const TYPE_ICONS = {
  people: Person,
  status: StatusIcon,
  date: Calendar,
  numbers: Numbers,
  dropdown: DropdownIcon,
  timeline: TimelineIcon,
  checkbox: CheckboxIcon,
  rating: Favorite,
  link: LinkIcon,
  text: TextIcon,
  long_text: TextIcon,
};

function TypeIcon({ type }) {
  const Icon = TYPE_ICONS[canonicalType(type)] || ColumnIcon;
  return (
    <span className="spt-colicon">
      <Icon size="14" />
    </span>
  );
}

const valueFor = (row, columnId) =>
  (row.column_values || []).find((cv) => String(cv.id) === String(columnId));

function StatusCell({ column, rawValue, onClick }) {
  const draft = rawValue ? parseColumnValue(rawValue, column) : null;
  if (!draft) {
    return (
      <div
        className={`spt-statuscell spt-blank ${onClick ? 'spt-editable' : ''}`}
        onClick={onClick}
        role={onClick ? 'button' : undefined}
      />
    );
  }
  const color = statusColorFor(column, draft);
  return (
    <div
      className={`spt-statuscell ${color ? '' : 'spt-status-unknown'} ${onClick ? 'spt-editable' : ''}`}
      style={color ? { backgroundColor: color } : undefined}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
    >
      {draft.label}
    </div>
  );
}

/** monday's default group blue: the bar of every subitem template. */
export const DEFAULT_GROUP_COLOR = '#579bfc';

const SUBITEM_LABELS = { first: 'Subitem', add: '+ Add subitem', placeholder: 'Write a subitem name', deleteRow: 'Delete subitem' };

export default function SubitemsTable({
  columns = [],
  subitems = [],
  readOnly = true,
  // editable-mode callbacks (each optional; absent = feature hidden)
  onNameChange, // (rowId, name)
  onRowDelete, // (rowId)
  onRowsReorder, // (fromIndex, toIndex)
  onAddRow, // ()
  onCellClick, // (rowId, column, anchorEl)
  onAddColumnClick, // (anchorEl)
  onColumnMenuClick, // (column, anchorEl)
  onOpenUpdates, // (rowId) — editor only; opens the row's updates panel
  cellUploads, // (rowId, column) → that File cell's running uploads (lib/cellUploads); editor only
  parentColumns, // the parent item's board columns: the names' "+ Value" menu offers their values
  parent, // the parent item (lib/parentItem) for those values' previews, or null
  onCreateNow, // (row, buttonEl) — read-only only: a "+ Create now" button on each row's hover
  invalidNameIds, // Set of row ids whose name is required but blank (outlined red)
  invalidUpdateIds, // Set of row ids with an empty update (their Updates pill turns red)
  // The group template reuses this table for its ITEMS: other words, a
  // subitem-count badge by the name, and a chevron per row that expands into
  // whatever renderExpanded returns (its subitems table). Expanded rows and
  // drag-reorder do not mix, so an expandable table is never passed onRowsReorder.
  labels = SUBITEM_LABELS, // { first, add, placeholder, deleteRow }
  subitemBadge, // { count(row) } — monday's subitem bubble by the name; clicking it expands the row
  expandedIds, // Set of row ids currently expanded
  onToggleExpand, // (rowId)
  renderExpanded, // (row) → content for the expanded row
  groupColor = DEFAULT_GROUP_COLOR, // the left bar's colour
  nested = false, // a table inside an expanded row: lighter bar
}) {
  const editable = !readOnly;
  // Guided-tour anchors (lib/tours) — on the editor's top-level table only, so
  // the read-only preview and an expanded item's nested table never duplicate them.
  const tourAnchor = (name) => (editable && !nested ? name : undefined);
  const expandable = Boolean(expandedIds && onToggleExpand && renderExpanded);

  // An expanded row's content takes the board's VISIBLE width and stays in view
  // while the board scrolls sideways (.spt-expanded-inner), rather than spanning
  // every item column. An item's subitems table then opens where the user is
  // looking, with its add-column button in reach.
  const wrapRef = useRef(null);
  const [viewWidth, setViewWidth] = useState(null);
  useEffect(() => {
    const el = wrapRef.current;
    if (!expandable || !el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setViewWidth(el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, [expandable]);

  const wrapProps = {
    ref: wrapRef,
    className: `spt-tablewrap spt-grouped${nested ? ' spt-nested' : ''}`,
    style: {
      '--spt-group-color': groupColor || DEFAULT_GROUP_COLOR,
      ...(viewWidth ? { '--spt-board-view': `${viewWidth}px` } : {}),
    },
  };
  const updatesCount = (row) => ((row && row.updates) || []).length;
  const showUpdates = Boolean(editable && onOpenUpdates) || subitems.some((r) => updatesCount(r) > 0);

  // The subitem count as a grey chip right after the name (monday's style), and
  // a second way to expand the row. No subitems, no chip.
  const badgeFor = (row) => {
    if (!subitemBadge || !expandable) return null;
    const n = subitemBadge.count(row);
    if (!n) return null;
    const open = expandedIds.has(row.id);
    return (
      <button
        type="button"
        className="spt-subitem-badge"
        aria-expanded={open}
        aria-label={`${n} subitem${n === 1 ? '' : 's'}, ${open ? 'hide' : 'show'}`}
        title={`${n} subitem${n === 1 ? '' : 's'}`}
        onClick={() => onToggleExpand(row.id)}
      >
        {n}
      </button>
    );
  };

  // The row's updates slot: monday's add-update bubble while it has none, the
  // update bubble with its count once it has some. In the editor it opens the panel.
  const updatesSlot = (row, index) => {
    const n = updatesCount(row);
    const label = `${n} update${n === 1 ? '' : 's'}`;
    const icon = n ? (
      <span className="spt-updates-icon">
        <UpdateIcon size="22" aria-hidden="true" />
        <span className="spt-updates-count">{n}</span>
      </span>
    ) : (
      <AddUpdate size="22" aria-hidden="true" />
    );
    let content = null;
    if (editable && onOpenUpdates) {
      const invalid = invalidUpdateIds && invalidUpdateIds.has(row.id);
      content = (
        <button
          type="button"
          className={`spt-updates-btn${n ? ' has-updates' : ''}${invalid ? ' spt-updates-invalid' : ''}`}
          onClick={() => onOpenUpdates(row.id)}
          aria-label={n ? `${label}, edit` : 'Add an update'}
          title={n ? `${label}. Edit them` : 'Add an update to post when this row is created'}
          data-tour={index === 0 ? tourAnchor('updates') : undefined}
        >
          {icon}
        </button>
      );
    } else if (n) {
      content = (
        <span className="spt-updates-btn has-updates" role="img" aria-label={label} title={label}>
          {icon}
        </span>
      );
    }
    return <span className="spt-updates-slot">{content}</span>;
  };

  // every column the table renders, for the colSpan of an expanded row
  const columnCount = 1 + columns.length + (editable && onAddColumnClick ? 1 : 0);

  const header = (
    <thead>
      <tr>
        <th className="spt-first">
          <div className="spt-firsthead">
            <span className="spt-rownum-gutter">#</span>
            <span>{labels.first}</span>
          </div>
        </th>
        {columns.map((c) => (
          <th key={c.id} style={{ minWidth: 130 }}>
            <div className="spt-colhead">
              <TypeIcon type={c.type} />
              <span>
                {c.title}
                {c.settings && c.settings.unit ? ` (${c.settings.unit})` : ''}
              </span>
            </div>
            {editable && onColumnMenuClick && (
              <span className="spt-colmenu-btn">
                <IconButton
                  size="xs"
                  icon={DropdownChevronDown}
                  aria-label={`Column menu for ${c.title}`}
                  onClick={(e) => onColumnMenuClick(c, e.currentTarget)}
                />
              </span>
            )}
          </th>
        ))}
        {editable && onAddColumnClick && (
          <th className="spt-addcol-th" data-tour={tourAnchor('addcol')}>
            <IconButton
              size="small"
              icon={AddSmall}
              aria-label="Add column"
              onClick={(e) => onAddColumnClick(e.currentTarget)}
            />
          </th>
        )}
      </tr>
    </thead>
  );

  const renderRow = (row, index, dragProvided) => (
    <tr
      key={row.id}
      ref={dragProvided ? dragProvided.innerRef : undefined}
      {...(dragProvided ? dragProvided.draggableProps : {})}
    >
      <td className="spt-first">
        <div className="spt-firstcell" data-tour={index === 0 ? tourAnchor('row-name') : undefined}>
          <span className="spt-rownum">{index + 1}</span>
          {editable && onRowsReorder ? (
            <span className="spt-draghandle" {...(dragProvided ? dragProvided.dragHandleProps : {})}>
              <Drag size="14" />
            </span>
          ) : null}
          {/* read-only, an item with no subitems has nothing to expand */}
          {expandable && !editable && subitemBadge && !subitemBadge.count(row) && <span className="spt-expand-gap" aria-hidden="true" />}
          {expandable && (editable || !subitemBadge || subitemBadge.count(row) > 0) && (
            <button
              type="button"
              className="spt-expand-btn"
              aria-expanded={expandedIds.has(row.id)}
              aria-label={expandedIds.has(row.id) ? `Collapse ${row.name || 'row'}` : `Expand ${row.name || 'row'}`}
              onClick={() => onToggleExpand(row.id)}
            >
              {expandedIds.has(row.id) ? <DropdownChevronDown size="16" /> : <DropdownChevronRight size="16" />}
            </button>
          )}
          {editable && onNameChange ? (
            <DynamicTextField
              variant="bare"
              adornment={badgeFor(row)}
              value={row.name || ''}
              placeholder={labels.placeholder}
              ariaLabel={`${labels.first} ${index + 1} name`}
              invalid={Boolean(invalidNameIds && invalidNameIds.has(row.id))}
              parentColumns={parentColumns}
              parent={parent}
              onChange={(v) => onNameChange(row.id, v)}
            />
          ) : (
            <>
              <span className="spt-nametext" title={row.name}>
                <TokenText value={row.name} />
              </span>
              {badgeFor(row)}
              <span className="spt-name-spacer" />
            </>
          )}
          {!editable && onCreateNow && (
            <button type="button" className="spt-createnow" onClick={(e) => onCreateNow(row, e.currentTarget)} title={`Create “${row.name || 'this row'}” on this board now`}>
              <AddSmall size="16" aria-hidden="true" />
              Create now
            </button>
          )}
          {editable && onRowDelete && (
            <span className="spt-rowdelete">
              <IconButton
                size="xs"
                icon={Delete}
                aria-label={labels.deleteRow}
                hideTooltip
                onClick={() => onRowDelete(row.id)}
              />
            </span>
          )}
          {showUpdates && updatesSlot(row, index)}
        </div>
      </td>
      {columns.map((c) => {
        const raw = valueFor(row, c.id);
        const clickable = editable && onCellClick;
        if (canonicalType(c.type) === 'status') {
          return (
            <td key={c.id} className="spt-statuscol">
              <StatusCell
                column={c}
                rawValue={raw}
                onClick={clickable ? (e) => onCellClick(row.id, c, e.currentTarget) : undefined}
              />
            </td>
          );
        }
        const leftAlign = ['text', 'long_text'].includes(canonicalType(c.type));
        return (
          <td key={c.id}>
            <div
              className={`spt-cell ${leftAlign ? 'spt-cell-left' : ''} ${clickable ? 'spt-editable' : ''}`}
              onClick={clickable ? (e) => onCellClick(row.id, c, e.currentTarget) : undefined}
              role={clickable ? 'button' : undefined}
            >
              <CellContent column={c} rawValue={raw} uploads={cellUploads && canonicalType(c.type) === 'file' ? cellUploads(row.id, c) : undefined} />
            </div>
          </td>
        );
      })}
      {editable && onAddColumnClick && <td className="spt-addcol-th" />}
    </tr>
  );

  // The expanded row sits right under its item, spanning the whole table.
  const renderRowWithExpansion = (row, index) => (
    <React.Fragment key={row.id}>
      {renderRow(row, index, null)}
      {expandable && expandedIds.has(row.id) && (
        <tr className="spt-expanded-row">
          <td className="spt-expanded-cell" colSpan={columnCount}>
            <div className="spt-expanded-inner">{renderExpanded(row)}</div>
          </td>
        </tr>
      )}
    </React.Fragment>
  );

  const addRow = editable && onAddRow && (
    <tr>
      <td className="spt-first spt-addrow-cell" onClick={onAddRow}>
        <div className="spt-addrow-inner">
          <span className="spt-rownum" />
          <span>{labels.add}</span>
        </div>
      </td>
      {columns.map((c) => (
        <td key={c.id} />
      ))}
      {onAddColumnClick && <td className="spt-addcol-th" />}
    </tr>
  );

  if (editable && onRowsReorder) {
    return (
      <div {...wrapProps}>
        <DragDropContext
          onDragEnd={(result) => {
            if (!result.destination) return;
            onRowsReorder(result.source.index, result.destination.index);
          }}
        >
          <table className="spt-board">
            {header}
            <Droppable droppableId="spt-rows">
              {(dropProvided) => (
                <tbody ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
                  {subitems.map((row, index) => (
                    <Draggable key={String(row.id)} draggableId={String(row.id)} index={index}>
                      {(dragProvided) => renderRow(row, index, dragProvided)}
                    </Draggable>
                  ))}
                  {dropProvided.placeholder}
                  {addRow}
                </tbody>
              )}
            </Droppable>
          </table>
        </DragDropContext>
      </div>
    );
  }

  return (
    <div {...wrapProps}>
      <table className="spt-board">
        {header}
        <tbody>
          {subitems.map((row, index) => (expandable ? renderRowWithExpansion(row, index) : renderRow(row, index, null)))}
          {addRow}
        </tbody>
      </table>
    </div>
  );
}
